import ky from 'ky';
import cache from 'helpers/fetchCache';
import * as Sentry from '@sentry/react';
import getRuntimeEnv from '../../env';

/** Check if it is allowed to cache the request with the current url
 * api/customEntityLists/me?expand=permissions is the route to get custom lists and it should not be cached for now
 * /byIds?expand= is the route to get entities's details depending on their types
 */
const checkAllowCacheRequest = (url: string): boolean =>
  url.indexOf('api/customEntityLists/me?expand=permissions') === -1 &&
  url.indexOf('/byIds?expand=') === -1 &&
  url.indexOf('/byIds?showGrouped=true') === -1 &&
  url.indexOf('/annotations/') === -1;

const kyWithSentry = ky.extend({
  /** The default timeout set by ky service was 10 sec, but tik tok kpi server requests are responded
   * with bigger time and that is why they are cancelled by ky service
   * To fix it, timeout was increased
   */
  timeout: 20000,
  fetch: async (input: RequestInfo, init?: RequestInit) => {
    const request = new Request(input, init);
    const requestCacheKey = `${request.url}_${init?.body}`;
    const cached = cache.get(requestCacheKey);

    if (cached) {
      const response = await cached;
      return response.clone();
    }

    const fetchPromise = (async (): Promise<Response> => {
      const response = await fetch(request);

      // Or return a `Response` instance to overwrite the response.
      if (response.ok) {
        if (checkAllowCacheRequest(request.url)) {
          cache.set(requestCacheKey, response.clone());
        }
      } else {
        // Log to console
        // eslint-disable-next-line no-console
        console.error(
          'An error occurred in an API request.',
          new Error(response.statusText),
          { response: response, requestUrl: request.url }
        );

        // Report to Sentry
        if (getRuntimeEnv().REACT_APP_SENTRY_DSN) {
          Sentry.captureException(new Error(response.statusText));
        }
      }

      return response;
    })();

    if (checkAllowCacheRequest(request.url)) {
      cache.set(requestCacheKey, fetchPromise);
    }
    return fetchPromise;
  },
});

export default kyWithSentry;
